/**
 * Lock Takeover Handlers
 * 
 * Handles takeover request actions (approve, deny, cancel) and request creation
 * via Turbo Stream and JSON responses.
 * 
 * Functions are made globally available so they can be called from onclick handlers.
 */

// Make functions globally available
window.handleTakeoverAction = function(event, requestId, action, postId) {
  // Prevent form submission and default button behavior
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  console.log('handleTakeoverAction called:', { requestId, action, postId });
  
  // Disable button while processing (using data-disable-with if available)
  const button = event?.target;
  let originalText = '';
  let originalDisabled = false;
  if (button) {
    originalText = button.textContent || button.innerText || '';
    originalDisabled = button.disabled;
    button.disabled = true;
    // Use data-disable-with attribute value if available
    const disableWithText = button.getAttribute('data-disable-with') || originalText;
    button.textContent = disableWithText;
    button.style.cursor = 'not-allowed';
    button.style.opacity = '0.6';
  }
  
  const actionMap = {
    'approve': 'approve_takeover',
    'deny': 'deny_takeover',
    'cancel': 'cancel_takeover'
  };
  const endpoint = actionMap[action];
  if (!endpoint) {
    console.error('Invalid action:', action);
    // Re-enable button on error
    if (button) {
      button.disabled = originalDisabled;
      button.textContent = originalText;
      button.style.cursor = '';
      button.style.opacity = '';
    }
    return false;
  }
  
  const url = `/admin/posts/${postId}/${endpoint}`;
  console.log('Making request to:', url);
  
  const formData = new FormData();
  formData.append('request_id', requestId);
  
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (!csrfToken) {
    console.error('CSRF token not found');
    alert('Security token not found. Please refresh the page.');
    // Re-enable button on error
    if (button) {
      button.disabled = originalDisabled;
      button.textContent = originalText;
      button.style.cursor = '';
      button.style.opacity = '';
    }
    return false;
  }
  
  // For cancel action, prefer Turbo Stream to update the UI
  // For approve/deny, use JSON (approve redirects, deny shows notification)
  const acceptHeader = action === 'cancel' 
    ? 'text/vnd.turbo-stream.html, text/html, application/json'
    : 'application/json';
  
  fetch(url, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': acceptHeader,
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: formData,
    credentials: 'same-origin',
    redirect: action === 'approve' ? 'follow' : 'manual' // Only follow redirects for approve, not for deny/cancel
  })
  .then(response => {
    console.log('Response status:', response.status);
    console.log('Response type:', response.type);
    console.log('Response headers:', response.headers.get('content-type'));
    console.log('Action:', action);
    
    // For deny/cancel actions, never follow redirects - always expect JSON or Turbo Stream
    if ((action === 'deny' || action === 'cancel') && response.type === 'opaqueredirect') {
      console.warn('⚠️ Redirect detected for', action, 'action - ignoring redirect');
      return { success: true, redirect: false, message: 'Request processed successfully.' };
    }
    
    const contentType = response.headers.get('content-type');
    
    // Check if response is turbo_stream (for cancel action)
    if (contentType && contentType.includes('text/vnd.turbo-stream')) {
      return response.text().then(text => {
        // Handle turbo_stream response
        console.log('✅ Turbo stream response received for', action);
        console.log('  - Stream content length:', text.length);
        
        // Execute turbo stream - this will automatically update the DOM
        if (window.Turbo && window.Turbo.renderStreamMessage) {
          try {
            window.Turbo.renderStreamMessage(text);
            console.log('✅ Turbo stream executed successfully');
          } catch (e) {
            console.error('❌ Turbo stream execution failed:', e);
            // Fallback: manually parse and update
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/html');
            const turboStreams = doc.querySelectorAll('turbo-stream');
            turboStreams.forEach(stream => {
              const streamAction = stream.getAttribute('action');
              const target = stream.getAttribute('target');
              const template = stream.querySelector('template');
              if (target && template) {
                const targetElement = document.getElementById(target);
                if (targetElement) {
                  if (streamAction === 'replace') {
                    targetElement.innerHTML = template.innerHTML;
                  } else if (streamAction === 'update') {
                    targetElement.innerHTML = template.innerHTML;
                  } else if (streamAction === 'append') {
                    targetElement.insertAdjacentHTML('beforeend', template.innerHTML);
                  }
                  console.log(`✅ Manually executed ${streamAction} on #${target}`);
                }
              }
            });
          }
        } else {
          console.warn('⚠️ Turbo not available, manually parsing stream');
          // Fallback: manually parse and update
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');
          const turboStreams = doc.querySelectorAll('turbo-stream');
          turboStreams.forEach(stream => {
            const streamAction = stream.getAttribute('action');
            const target = stream.getAttribute('target');
            const template = stream.querySelector('template');
            if (target && template) {
              const targetElement = document.getElementById(target);
              if (targetElement) {
                if (streamAction === 'replace') {
                  targetElement.innerHTML = template.innerHTML;
                } else if (streamAction === 'update') {
                  targetElement.innerHTML = template.innerHTML;
                } else if (streamAction === 'append') {
                  targetElement.insertAdjacentHTML('beforeend', template.innerHTML);
                }
                console.log(`✅ Manually executed ${streamAction} on #${target}`);
              }
            }
          });
        }
        return { success: true, format: 'turbo_stream', content: text };
      });
    }
    
    // Check if response is JSON
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    } else {
      // If not JSON, might be HTML redirect
      console.warn('Response is not JSON, content-type:', contentType);
      // Only follow redirects for approve action, not for deny/cancel
      if (response.redirected && action === 'approve') {
        window.location.href = response.url;
        return { success: true, redirected: true };
      } else if (response.redirected || response.type === 'opaqueredirect') {
        // For deny/cancel, don't follow redirects
        console.log('⚠️ Response redirected but ignoring for', action, 'action');
        return { success: true, redirect: false, message: 'Request processed successfully.' };
      }
      return response.text().then(text => {
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned non-JSON response');
      });
    }
  })
  .then(data => {
    console.log('Response data:', data);
    console.log('Action:', action);
    
    // Handle Turbo Stream responses (for cancel action)
    if (data.format === 'turbo_stream') {
      console.log('✅ Turbo Stream handled for', action, '- DOM should be updated automatically');
      // Turbo Stream already updated the DOM, no additional action needed
      // The lock-request-status div should now show the cancelled state
      return;
    }
    
    if (data.success || data.redirected) {
      if (action === 'approve' && data.redirect_url) {
        console.log('✅ Approve: Redirecting owner to show page:', data.redirect_url);
        // Redirect owner to show page
        setTimeout(() => {
          window.location.href = data.redirect_url;
        }, 500);
      } else if (action === 'deny') {
        console.log('✅ Deny: Request denied, staying on page. Polling will update UI.');
        // For deny, show a brief success message
        // Polling will update the UI to show denied state
        const notification = document.getElementById('lock-status-notification');
        if (notification) {
          notification.innerHTML = `<div style="padding: 10px; background-color: #d4edda; border: 1px solid #28a745; border-radius: 4px; color: #155724; margin: 10px 0;">Request denied successfully.</div>`;
          setTimeout(() => {
            notification.innerHTML = '';
          }, 3000);
        }
      } else if (action === 'cancel') {
        // This should have been handled by Turbo Stream above, but handle JSON fallback
        console.log('✅ Cancel: Request cancelled via JSON response');
        // Polling will update the UI to show cancelled state
      }
      // Polling will handle UI updates for deny and cancel
      console.log('⏳ Polling will update UI if needed...');
    } else {
      alert(data.message || 'An error occurred');
    }
  })
  .catch(error => {
    console.error('Error details:', error);
    console.error('Error stack:', error.stack);
    alert('An error occurred while processing your request: ' + (error.message || 'Unknown error'));
    // Re-enable button on error
    if (button) {
      button.disabled = originalDisabled;
      button.textContent = originalText;
      button.style.cursor = '';
      button.style.opacity = '';
    }
  });
  
  return false;
};

window.handleRequestTakeover = function(event, postId) {
  // Prevent default if event is provided
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  console.log('handleRequestTakeover called:', { postId });
  
  const url = `/admin/posts/${postId}/request_takeover`;
  console.log('Making request to:', url);
  
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (!csrfToken) {
    console.error('CSRF token not found');
    alert('Security token not found. Please refresh the page.');
    return false;
  }

  // Disable button while processing
  const button = event?.target || document.getElementById('request-takeover-button') || document.querySelector('button[onclick*="handleRequestTakeover"]');
  const originalText = button ? button.textContent : 'Request to Take Over Editing';
  if (button) {
    button.disabled = true;
    button.textContent = 'Sending request...';
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Accept': 'text/vnd.turbo-stream.html, text/html, application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    credentials: 'same-origin'
  })
  .then(response => {
    console.log('Response status:', response.status);
    console.log('Response type:', response.type);
    console.log('Response headers:', response.headers.get('content-type'));
    
    const contentType = response.headers.get('content-type');
    
    // Check if response is turbo_stream
    if (contentType && contentType.includes('text/vnd.turbo-stream')) {
      return response.text().then(text => {
        // Handle turbo_stream response
        console.log('✅ Turbo stream response received');
        console.log('  - Stream content length:', text.length);
        
        // Execute turbo stream - this will automatically update the DOM
        const executeTurboStream = () => {
          if (window.Turbo && window.Turbo.renderStreamMessage) {
            try {
              window.Turbo.renderStreamMessage(text);
              console.log('✅ Turbo stream executed successfully');
              return true;
            } catch (e) {
              console.error('❌ Turbo stream execution failed:', e);
              return false;
            }
          }
          return false;
        };
        
        const executeManually = () => {
          // Fallback: manually parse and update
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');
          const turboStreams = doc.querySelectorAll('turbo-stream');
          turboStreams.forEach(stream => {
            const action = stream.getAttribute('action');
            const target = stream.getAttribute('target');
            const template = stream.querySelector('template');
            if (target && template) {
              const targetElement = document.getElementById(target);
              if (targetElement) {
                if (action === 'replace') {
                  targetElement.innerHTML = template.innerHTML;
                } else if (action === 'update') {
                  targetElement.innerHTML = template.innerHTML;
                } else if (action === 'append') {
                  targetElement.insertAdjacentHTML('beforeend', template.innerHTML);
                }
                console.log(`✅ Manually executed ${action} on #${target}`);
              }
            }
          });
        };
        
        // Try Turbo first, fallback to manual
        if (!executeTurboStream()) {
          console.warn('⚠️ Turbo not available, manually parsing stream');
          executeManually();
        }
        
        // After Turbo Stream updates the DOM, trigger polling controller to check immediately
        // This ensures the new request is detected and status polling starts
        setTimeout(() => {
          // Find the polling controller element
          const pollingElement = document.querySelector('[data-controller*="post-lock-polling"]');
          if (pollingElement) {
            // Try to access Stimulus controller via the element's dataset
            // Stimulus stores controller instances on the element
            let controller = null;
            
            // Method 1: Try accessing via Stimulus application (if available globally)
            if (window.Stimulus) {
              try {
                controller = window.Stimulus.getControllerForElementAndIdentifier(
                  pollingElement,
                  'post-lock-polling'
                );
              } catch (e) {
                console.log('Could not access controller via window.Stimulus');
              }
            }
            
            // Method 2: Try accessing via element's controller property (Stimulus 3+)
            if (!controller && pollingElement.controller) {
              controller = pollingElement.controller;
            }
            
            // Method 3: Dispatch custom event as fallback
            if (!controller) {
              console.log('🔄 Dispatching custom event to trigger poll (controller not directly accessible)');
              pollingElement.dispatchEvent(new CustomEvent('trigger-poll', { bubbles: true }));
            } else {
              console.log('🔄 Triggering immediate poll after Turbo Stream update');
              // Reset previousState to force update detection
              controller.previousState = null;
              // Trigger immediate poll
              if (controller.performPoll) {
                controller.performPoll();
              } else {
                // Fallback to event
                pollingElement.dispatchEvent(new CustomEvent('trigger-poll', { bubbles: true }));
              }
            }
          } else {
            console.warn('⚠️ Polling controller element not found');
          }
        }, 200); // Small delay to ensure DOM is fully updated
        return { success: true, format: 'turbo_stream', content: text };
      });
    }
    
    // Check if response is JSON
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    
    // Check if response is HTML
    if (contentType && contentType.includes('text/html')) {
      return response.text().then(text => {
        return { success: true, format: 'html', content: text };
      });
    }
    
    // If no content type or unexpected, try to parse as text
    return response.text().then(text => {
      console.warn('Unexpected response type:', contentType, 'trying to parse as text');
      // Try to extract lock-request-status from HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const statusDiv = doc.getElementById('lock-request-status');
      if (statusDiv) {
        return { success: true, format: 'html', content: statusDiv.outerHTML };
      }
      return { success: true, format: 'text', content: text };
    });
  })
  .then(data => {
    console.log('Response data:', data);
    
    // If Turbo Stream was executed, the DOM should already be updated
    // Turbo Stream will replace the lock-request-status div with the new status
    if (data.format === 'turbo_stream') {
      console.log('✅ Turbo Stream handled - DOM should be updated automatically');
      // No need to manually update - Turbo Stream already did it
      // The lock-request-status div should now show the pending request status
    } else if (data.success !== false) {
      // Fallback for non-Turbo Stream responses (shouldn't happen, but handle it)
      console.log('⚠️ Non-Turbo Stream response, manually updating');
      const requestStatusDiv = document.getElementById('lock-request-status');
      if (requestStatusDiv && (data.format === 'html' || data.format === 'text')) {
        // Parse HTML and extract the lock-request-status content
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.content, 'text/html');
        const newStatus = doc.getElementById('lock-request-status');
        if (newStatus) {
          requestStatusDiv.innerHTML = newStatus.innerHTML;
        } else {
          // If no lock-request-status found, try to use the body content
          const bodyContent = doc.body.innerHTML;
          if (bodyContent) {
            requestStatusDiv.innerHTML = bodyContent;
          }
        }
      }
    } else {
      console.error('❌ Request failed:', data.message);
      alert(data.message || 'An error occurred');
      // Re-enable button on error
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  })
  .catch(error => {
    console.error('Error details:', error);
    console.error('Error stack:', error.stack);
    alert('An error occurred while processing your request: ' + (error.message || 'Unknown error'));
    // Re-enable button on error
    if (button) {
      button.disabled = false;
      button.textContent = originalText;
    }
  });
  
  return false;
};

